import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { motion, AnimatePresence } from "framer-motion";
import { analytics } from "@/lib/analytics";
import { useSubscription } from "@/hooks/useSubscription";
import { LIVE_REQUIRES_PREMIUM } from "@/lib/liveAccess";
import MobileLayout from "@/components/MobileLayout";
import {
  ArrowRight,
  Radio,
  Sparkles,
  Target,
  ChevronRight,
  Moon,
  Brain,
  Zap,
  Flame,
  Swords,
  BookOpen,
  GripVertical,
  Rocket,
  Heart,
  Dumbbell,
  Apple,
  Settings,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useCriticalAlerts } from "@/hooks/useCriticalAlerts";
import { useChallenges } from "@/hooks/useChallenges";
import { CHALLENGE_ENABLED } from "@/lib/featureFlags";
import { useMirrorScore } from "@/hooks/useMirrorScore";
import { useNudges } from "@/hooks/useNudges";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES, heroContentId } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import NudgeCard from "@/components/NudgeCard";
import vyvIcon from "@/assets/vyv-icon.png";
import AvatarViewer from "@/components/AvatarViewer";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import { FULL_BODY_IS_AVATURN_MODEL } from "@/components/avatar/miniMirrorCalibration";
import MirrorScore from "@/components/MirrorScore";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import { useDailyBrief } from "@/hooks/useDailyBrief";
import { useAppSplash } from "@/contexts/AppSplashContext";
import { DailyBriefCard } from "@/components/home/DailyBriefCard";
import { ProactiveHomeCard } from "@/components/proactive/ProactiveHomeCard";
import HomeTodayPanel, { type HomeTodayEvent } from "@/components/home/HomeTodayPanel";
import { useCheckIn } from "@/hooks/useCheckIn";
import { checkInEnergyLabel } from "@/lib/checkInDates";
import { useHomeAgenda } from "@/hooks/useHomeAgenda";
import WhatsAppLinkCard from "@/components/connections/WhatsAppLinkCard";
import { isIOSNative, isPaymentUiBlocked } from "@/lib/platform";
import { capitalizeName } from "@/lib/formatName";
import { sfx } from "@/lib/sfx";


const MIRROR_BG = "#06111C";

const focusKeys = [
  { focus: "focus.clarity.title", action: "focus.clarity.action", guidance: "focus.clarity.guidance" },
  { focus: "focus.deepwork.title", action: "focus.deepwork.action", guidance: "focus.deepwork.guidance" },
  { focus: "focus.presence.title", action: "focus.presence.action", guidance: "focus.presence.guidance" },
  { focus: "focus.patience.title", action: "focus.patience.action", guidance: "focus.patience.guidance" },
  { focus: "focus.discipline.title", action: "focus.discipline.action", guidance: "focus.discipline.guidance" },
];


// â”€â”€ Hero message cache â”€â”€
const HERO_CACHE_KEY = "vyv-hero-message";
const HERO_CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours

// A frase de fallback do hero morava aqui, num Record de três idiomas — copy
// invisível para o i18n:check e para o tradutor. Agora é chave de locale como
// qualquer outro texto: "mirror.hero.fallback".

// â”€â”€ Explore Modules Card with canvas particles â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const EXPLORE_PARTICLE_COUNT = 20;
const EXPLORE_COLORS = ["#00e5ff", "#7c3aed"];

const ExploreModulesCard: React.FC<{ navigate: (path: string) => void; t: (key: string) => string }> = React.memo(({ navigate, t }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.parentElement?.getBoundingClientRect();
    if (!rect) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + "px";
    canvas.style.height = rect.height + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const w = rect.width;
    const h = rect.height;
    const particles = Array.from({ length: EXPLORE_PARTICLE_COUNT }, (_, i) => {
      const speed = 0.2 + Math.random() * 0.4;
      const angle = Math.random() * Math.PI * 2;
      return {
        x: Math.random() * w, y: Math.random() * h,
        size: 1 + Math.random(), opacity: 0.4 + Math.random() * 0.3,
        vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        color: EXPLORE_COLORS[i % 2],
      };
    });

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const MODULE_ICONS = [Heart, Target, Zap, Moon, Dumbbell, Apple];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate("/modules")}
      className="w-full rounded-2xl overflow-hidden relative mb-5 cursor-pointer active:scale-[0.98] transition-transform"
      style={{
        height: 140,
        background: "linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.40) 100%), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_011.jpeg) center/cover no-repeat",
        border: "1px solid rgba(0,229,255,0.25)",
      }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 rounded-2xl" style={{ pointerEvents: "none", zIndex: 0 }} />

      {/* Icon row */}
      <div className="flex items-center justify-center gap-4 pt-6 relative z-[1]">
        {MODULE_ICONS.map((Icon, i) => (
          <Icon key={i} size={14} style={{ color: "#00e5ff", filter: "drop-shadow(0 0 6px rgba(0,229,255,0.6))" }} />
        ))}
      </div>

      {/* Bottom bar: title + CTA */}
      <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-5 z-[1]"
        style={{ background: "linear-gradient(to top, rgba(0,0,0,0.60) 0%, transparent 100%)" }}>
        <div className="flex items-center justify-between">
          <p className="font-display text-[13px] font-bold" style={{ color: "#ffffff", textShadow: "0 0 20px rgba(0,229,255,0.3)" }}>
            {t("home.exploreModules")}
          </p>
          <div className="flex items-center gap-1 font-display" style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.25), rgba(0,229,255,0.10))", border: "1px solid rgba(0,229,255,0.50)", borderRadius: "20px", padding: "5px 12px", color: "#00e5ff", fontSize: "11px", fontWeight: "600", boxShadow: "0 0 12px rgba(0,229,255,0.20)", backdropFilter: "blur(4px)" }}>
            {t("home.exploreAll")} <ArrowRight size={11} />
          </div>
        </div>
      </div>
    </motion.div>
  );
});
ExploreModulesCard.displayName = "ExploreModulesCard";

const MirrorHome = () => {
  const navigate = useNavigate();
  // "Ativar WhatsApp" spotlight — a landing WhatsApp click stores a timestamped
  // vyv_intent flag (the /app gate drops the URL query, so localStorage carries
  // it across the auth flow). Consumed ONCE on the first Home load: scroll to +
  // briefly ring the existing WhatsAppLinkCard, then clear the flag. Ignored if
  // older than 30 min so it never fires in an unrelated later session.
  const whatsappCardRef = useRef<HTMLDivElement>(null);
  const [highlightWhatsapp, setHighlightWhatsapp] = useState(false);
  useEffect(() => {
    let raw: string | null = null;
    try { raw = localStorage.getItem("vyv_intent"); } catch { /* private mode */ }
    let intent: string | null = null;
    let ts = 0;
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { intent?: string; ts?: number };
        intent = parsed?.intent ?? null;
        ts = typeof parsed?.ts === "number" ? parsed.ts : 0;
      } catch { /* corrupt/legacy value → ignore */ }
    }
    // Best-effort: a raw ?intent=whatsapp (rare direct hop to /home) counts as fresh.
    const url = new URLSearchParams(window.location.search);
    const urlIntent = url.get("intent");
    if (intent !== "whatsapp" && urlIntent === "whatsapp") { intent = "whatsapp"; ts = Date.now(); }

    // ONE-SHOT: clear the flag + URL param on this first load regardless of
    // outcome, so it can never re-fire on a later Home mount.
    if (raw) { try { localStorage.removeItem("vyv_intent"); } catch { /* ignore */ } }
    if (urlIntent) {
      url.delete("intent");
      const clean = window.location.pathname + (url.toString() ? "?" + url.toString() : "") + window.location.hash;
      window.history.replaceState(window.history.state, "", clean);
    }

    // Ignore a stale flag (>30 min).
    if (intent !== "whatsapp" || !ts || Date.now() - ts > 30 * 60 * 1000) return;

    // Delay so the card (and its own whatsapp_links load) has painted. No-op if
    // the user is already linked (card renders null → empty ref container).
    const t1 = window.setTimeout(() => {
      whatsappCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightWhatsapp(true);
    }, 500);
    const t2 = window.setTimeout(() => setHighlightWhatsapp(false), 3600);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, []);
  const { isPremium } = useSubscription();
  const { avatarUrl, loading: avatarLoading, hasAvatar } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície (bug 25/08). Predicado canônico: quem escolheu avatar sem ter
  // GLB próprio também cai na energia — nunca placeholder quebrado.
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;
  // Pre-fetch the daily brief here too — useDailyBrief is React Query backed
  // (cached forever within the session, deduped by queryKey), so calling it
  // here doesn't double-fetch with <DailyBriefCard /> below; it just lets
  // us read `loading` to know when the brief is ready.
  const { loading: briefLoading } = useDailyBrief();
  const splash = useAppSplash();
  const [userName, setUserName] = useState("");
  const [userGoal, setUserGoal] = useState("");
  const homeCheckIn = useCheckIn();
  const checkinStatus = homeCheckIn.isLoading ? "loading" : homeCheckIn.error ? "error" : homeCheckIn.isSignedOut ? "signed-out" : "ready";
  const todayCheckin = checkinStatus === "ready" ? homeCheckIn.todayCheckIn : null;
  const streak = checkinStatus === "ready" ? homeCheckIn.stats.streak : 0;
  const [infoOpen, setInfoOpen] = useState(false);
  const homeAgenda = useHomeAgenda();

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (infoOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [infoOpen]);

  // Dismiss the post-login branded splash as soon as the two critical
  // pieces of Home are ready: avatar (visual hero) + daily brief (top
  // content card). The context's 4s safety timer caps the wait if either
  // hook stalls. Cheap effect — no-op when splash isn't visible.
  useEffect(() => {
    if (!splash.visible) return;
    if (!avatarLoading && !briefLoading) {
      splash.hide();
    }
  }, [splash, avatarLoading, briefLoading]);
  const [heroMessage, setHeroMessage] = useState<string | null>(null);
  // A frase do hero e a UNICA superficie de IA do app sem linha no banco: ela
  // vive num cache de localStorage. `heroGenerated` separa o texto do modelo
  // do fallback de i18n (mirror.hero.fallback) — so o primeiro e denunciavel;
  // denunciar a propria copy nao informaria moderacao nenhuma.
  const [heroGenerated, setHeroGenerated] = useState(false);
  const { isReported } = useReportedContent();
  // Uma frase por usuario por dia local — a mesma janela do cache do hero.
  const heroContentIdToday = heroContentId(new Date().toISOString().slice(0, 10));
  const heroReported = isReported(REPORT_SURFACES.MIRROR_HERO, heroContentIdToday);
  const { lang, t, tf, tp } = useLanguage();
  const { hasUrgentTasks, urgentTasks } = useCriticalAlerts();
  const { activeChallenge, loading: challengeLoading } = useChallenges(t);
  const { score, contributors: scoreContributors, mirrorMessage, loading: scoreLoading } = useMirrorScore();

  const resolveMirrorMessage = (msg: string | null): string => {
    if (!msg) return "";
    const [key, param] = msg.split(":");
    const translated = t(key);
    return param ? translated.replace(/\{\{?count\}?\}/g, param) : translated;
  };
  const { activeNudge, dismissNudge, actOnNudge } = useNudges();
  const dayIndex = new Date().getDay();
  const hour = new Date().getHours();

  const todayFocus = focusKeys[dayIndex % focusKeys.length];

  useEffect(() => {
    const loadProfile = async () => {
      try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setHeroMessage(t("mirror.hero.fallback"));
        setHeroGenerated(false);
        return;
      }

      // â”€â”€ Hero message (fires concurrently, does not block profile load) â”€â”€
      void (async () => {
        // 1. Check cache
        try {
          const cached = localStorage.getItem(HERO_CACHE_KEY);
          if (cached) {
            const { message, timestamp, userId: cid, language: cachedLang } = JSON.parse(cached);
            if (cid === user.id && cachedLang === lang && Date.now() - timestamp < HERO_CACHE_TTL) {
              setHeroMessage(message);
              setHeroGenerated(true);
              return;
            }
          }
        } catch { /* ignore bad cache */ }

        // 2. Build task list from localStorage
        const tasks: string[] = (() => {
          try {
            const raw = localStorage.getItem("vyv-tasks");
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr)
              ? arr
                  .slice(0, 3)
                  .map((t: unknown) =>
                    typeof t === "string" ? t : (t as Record<string, string>)?.text || (t as Record<string, string>)?.title || ""
                  )
                  .filter(Boolean)
              : [];
          } catch { return []; }
        })();

        // 3. Fetch from edge function with 3s timeout
        const tod = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
        try {
          const { data, error } = await Promise.race([
            supabase.functions.invoke("mirror-chat", {
              body: {
                promptType: "daily_briefing", userId: user.id, timeOfDay: tod, language: lang, tasks,
                // Apple 3.1.1 — tells mirror-chat to omit price + checkout URL on iOS native.
                platform: isIOSNative() ? "ios" : "web",
              },
            }),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error("timeout")), 3000)
            ),
          ]);
          if (error || !data?.reply) throw new Error("empty");
          const msg = (data.reply as string).trim();
          setHeroMessage(msg);
          setHeroGenerated(true);
          try {
            localStorage.setItem(
              HERO_CACHE_KEY,
              JSON.stringify({ message: msg, timestamp: Date.now(), userId: user.id, language: lang })
            );
          } catch { /* storage full or unavailable */ }
        } catch {
          setHeroMessage(t("mirror.hero.fallback"));
          setHeroGenerated(false);
        }
      })();
      // â”€â”€ End hero message â”€â”€

      // Check-in and Agenda own their loading/retry cycles independently.
      const profileRes = await supabase.from("user_profiles")
        .select("first_name, primary_goal, onboarding_complete, onboarding_dismissed_forever")
        .eq("user_id", user.id).maybeSingle();

      const data = profileRes.data;
      if (data?.first_name) setUserName(capitalizeName(data.first_name));
      if (data?.primary_goal) setUserGoal(data.primary_goal);

    } catch (e) {
      console.error("[MirrorHome] Profile load error:", e);
      console.error(t("home.loadError") || "Error loading data");
    }
    };
    loadProfile();
  }, [lang]);

  const greeting =
    hour < 12
      ? t("greeting.morning")
      : hour < 18
      ? t("greeting.afternoon")
      : t("greeting.evening");

  const goalLabel = userGoal
    ? t(`goal.${userGoal}`) || userGoal.charAt(0).toUpperCase() + userGoal.slice(1)
    : "";

  const stagger = (i: number) => ({ delay: 0.08 + i * 0.08, duration: 0.6 });

  // "Preparar com o Espelho": seed a prep prompt (MirrorChat reads
  // sessionStorage 'vyv-prefill' into its input, :497) then open the Espelho.
  const prepareForMoment = (ev: HomeTodayEvent) => {
    try {
      const seed = tf("home.nextMoment.prepSeed", { title: ev.title });
      sessionStorage.setItem("vyv-prefill", seed);
    } catch { /* storage unavailable — Mirror still opens, just without the seed */ }
    navigate("/mirror");
  };

  const heroSubtitle =
    hour < 12
      ? t("home.hero.subtitleMorning")
      : hour < 18
      ? t("home.hero.subtitleAfternoon")
      : t("home.hero.subtitleEvening");

  const directionMain =
    hour < 12
      ? t("home.direction.mainMorning")
      : hour < 18
      ? t("home.direction.mainAfternoon")
      : t("home.direction.mainEvening");

  const directionSupport =
    hour < 12
      ? t("home.direction.supportMorning")
      : hour < 18
      ? t("home.direction.supportAfternoon")
      : t("home.direction.supportEvening");

  return (
    <MobileLayout className="pb-24 pt-8 relative overflow-hidden">

      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[6%] left-1/2 -translate-x-1/2 w-[420px] h-[320px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-[35%]"
          style={{ background: "linear-gradient(to top, hsl(var(--background)), transparent)" }}
        />
      </div>

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex items-center justify-between mb-6 relative z-50"
      >
        <div className="flex items-center gap-3">
          <MirrorBackButton />
          <button
            onClick={() => navigate("/settings")}
            className="group active:scale-[0.93] transition-all duration-300"
          >
            <img
              src={vyvIcon}
              alt="VYV"
              className="h-11 w-11 rounded-full object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300"
            />
          </button>
          <div>
            <p className="vyv-label mb-0.5">{greeting}</p>
            <h1 className="font-display text-[28px] font-semibold text-foreground leading-none">
              {userName || t("common.you")}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/guide")}
            aria-label={t("home.aboutVyv.ariaLabel")}
            className="flex items-center justify-center w-9 h-9 rounded-xl bg-card/60 border border-border/30 hover:border-primary/20 transition-all duration-300 group"
          >
            <BookOpen size={14} className="text-muted-foreground group-hover:text-primary/80 transition-colors" />
          </button>

          <button
            onClick={() => navigate("/settings")}
            aria-label={t("settings.title")}
            className="flex items-center justify-center w-9 h-9 rounded-xl bg-card/60 border border-border/30 hover:border-primary/20 transition-all duration-300 group"
          >
            <Settings size={14} className="text-muted-foreground group-hover:text-primary/80 transition-colors" />
          </button>

        </div>
      </motion.div>

      {/* Daily Brief V1 — morning Mirror context. Sits between header and
          smart nudge / hero. Renders nothing on error or empty state. */}
      <DailyBriefCard />
      {FEATURE_FLAGS.PROACTIVE_ASSISTANT_ENABLED && <ProactiveHomeCard />}

      {/* WhatsApp activation nudge — disappears once linked (management lives in
          Settings › Connections). Briefly ringed when arriving from the landing
          WhatsApp CTA (see the vyv_intent one-shot above). */}
      <div
        ref={whatsappCardRef}
        className={`mb-5 rounded-2xl transition-shadow duration-500 ${
          highlightWhatsapp ? "ring-2 ring-primary/70 shadow-[0_0_28px_rgba(0,229,255,0.35)]" : ""
        }`}
      >
        <WhatsAppLinkCard hideWhenConnected />
      </div>

      {/* Smart nudge — sits between header and hero; conditional render only */}
      {activeNudge && (
        <div className="mb-5">
          <NudgeCard
            nudge={activeNudge}
            onDismiss={dismissNudge}
            onAct={actOnNudge}
          />
        </div>
      )}

      {/* 1. MIRROR HERO */}
      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(0)}
        onClick={() => {
          // Sonic identity — the Mirror "awakening". Fires on the first user
          // engagement with the Mirror (this tap = a real gesture, so the iOS
          // WKWebView AudioContext can resume). Once/session via sfx.sonicLogo.
          sfx.sonicLogo();
          navigate("/mirror");
        }}
        className="w-full relative overflow-hidden rounded-2xl mb-5 group active:scale-[0.98] transition-transform duration-200 vyv-breathe"
        style={{
          backgroundColor: MIRROR_BG,
          border: "1.5px solid rgba(0,229,255,0.34)",
          animation: "mirrorHeroGlow 3.5s ease-in-out infinite",
          backgroundImage: "url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/pictures/VYVroomvazio.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Room image overlay */}
        <div className="absolute inset-0 rounded-2xl" style={{ background: "linear-gradient(135deg, rgba(6,17,28,0.30) 0%, rgba(6,17,28,0.20) 100%)" }} />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] rounded-full opacity-[0.09] group-hover:opacity-[0.16] transition-opacity duration-700"
          style={{ background: "radial-gradient(circle, hsl(187 72% 50%), transparent 70%)" }}
        />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

        <div className="flex items-end gap-4 pr-4 pb-2 pt-2 relative z-10">
          <div style={{
            position: "relative",
            width: "110px",
            // Container matched to the AvatarViewer canvas (222px) so the
            // full-body idle animation renders without overflow clipping —
            // feet anchored near the base, head near the top. The previous
            // 185px container cropped ~37px off the 222px canvas.
            height: "222px",
            flexShrink: 0,
            marginLeft: "8px",
            borderRadius: "12px",
            overflow: "hidden",
            background: "#06080f",
          }}>
            {/* Subtle floor glow */}
            <div style={{
              position: "absolute",
              bottom: "8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "70px",
              height: "16px",
              background: "radial-gradient(ellipse, rgba(0, 229, 255, 0.25) 0%, transparent 70%)",
              borderRadius: "50%",
              zIndex: 1,
              pointerEvents: "none" as const,
              filter: "blur(6px)",
            }} />
            {/* Subtle vertical body glow behind avatar */}
            <div style={{
              position: "absolute",
              top: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "60px",
              height: "75%",
              background: "radial-gradient(ellipse at center, rgba(0, 229, 255, 0.07) 0%, transparent 70%)",
              zIndex: 1,
              pointerEvents: "none" as const,
              filter: "blur(10px)",
            }} />
            {/* Avatar — Home Mirror card. Plays the Standing "W_Briefcase
                Idle" FBX animation. LEGACY-NAMING GUARD: isAvaturnModel
                stays true — it does NOT mean "Avaturn provider", it gates
                calibration profile A in the FROZEN AvatarCore. Catalog
                `.glb` files in /prerendered/ are Avaturn-origin geometry;
                MetaPerson dynamic (Premium v1.1+) gets its own profile
                when shipped. Do NOT flip false — see hotfix v3 notes in
                docs/avaturn-deprecation-discovery.md. */}
            <div style={{ position: "relative", zIndex: 2, width: "100%", height: "222px" }}>
              {!avatarLoading && !surfaceLoading && (
                showAvatar ? (
                  <AvatarViewer
                    modelUrl={avatarUrl || '/avatar/model_small.glb'}
                    height="222px"
                    enableRotate={false}
                    framing="full"
                    isAvaturnModel={FULL_BODY_IS_AVATURN_MODEL}
                    lookAtYOverride={1.3}
                    cameraDistanceOverride={4.7}
                  />
                ) : (
                  <VyvEnergy state="idle" ratio={0.5} interactive={false} />
                )
              )}
            </div>
          </div>

          <div className="flex-1 text-left self-start pt-4">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles size={11} className="text-primary" />
              <p className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/90" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>
                {t("home.hero.mirrorSession")}
              </p>
            </div>
            <p className="font-display text-[16px] font-semibold text-foreground leading-snug" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.8)" }}>
              {t("home.hero.talkToMirror")}
            </p>
            {heroMessage && !(heroGenerated && heroReported) ? (
              <p className="text-[11px] font-body text-foreground/80 leading-relaxed mt-1.5 italic line-clamp-2" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.9)" }}>
                {heroMessage}
              </p>
            ) : heroGenerated && heroReported ? null : (
              <div className="mt-1.5 h-2 w-3/4 rounded bg-primary/20 opacity-40 animate-pulse" />
            )}
            <div className="mt-2 flex items-center gap-2">
              <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${todayCheckin ? "border-primary/15 bg-primary/[0.06]" : "border-foreground/10 bg-foreground/[0.04]"}`}>
                <Zap size={9} className={todayCheckin ? "text-primary" : "text-foreground/50"} />
                <p className={`text-[9px] font-display tracking-wide ${todayCheckin ? "text-primary/90" : "text-foreground/55"}`}>
                  {checkinStatus === "loading" ? t("ux.loading") : checkinStatus !== "ready" ? t("home.glance.empty") : todayCheckin ? t("home.hero.checkedIn") : t("home.hero.notCheckedIn")}
                </p>
              </div>
            </div>
          </div>

          <div className="w-10 h-10 rounded-full bg-primary/12 flex items-center justify-center shrink-0 self-start mt-4 group-hover:bg-primary/20 transition-all duration-500 vyv-breathe">
            <ArrowRight size={15} className="text-primary" />
          </div>
        </div>
      </motion.button>

      {/* Denuncia da frase do hero. Fica FORA do <motion.button> do card: o
          card inteiro e um botao que navega pro Espelho, e botao dentro de
          botao e HTML invalido — o clique iria pro pai e o usuario cairia no
          chat em vez de abrir a denuncia. */}
      {heroGenerated && heroMessage && !heroReported && (
        <div className="w-full -mt-1 mb-1 flex justify-end">
          <ReportContentButton
            variant="inline"
            surface={REPORT_SURFACES.MIRROR_HERO}
            contentId={heroContentIdToday}
            snapshot={heroMessage}
            context={{ source: "home_hero" }}
          />
        </div>
      )}

      {/* LIVE ENTRY — push-to-talk full-screen presence (Option C). Aberto a
          TODOS enquanto LIVE_REQUIRES_PREMIUM for false (Rafa 30/08 — mesma
          cota do Espelho por voz, sem selo); com a constante em true o selo
          PREMIUM e o paywall voltam sozinhos. Ver @/lib/liveAccess. */}
      <button
        type="button"
        onClick={() => {
          if (!LIVE_REQUIRES_PREMIUM || isPremium) {
            navigate("/mirror/live");
          } else {
            analytics.paywallViewed("mirror_live");
            navigate("/pricing");
          }
        }}
        className="w-full -mt-2 mb-5 flex items-center justify-center gap-2 py-2.5 rounded-xl active:scale-[0.98] transition-transform"
        style={{
          background: "hsl(187 72% 50% / 0.08)",
          border: "1px solid hsl(187 72% 50% / 0.30)",
        }}
      >
        <Radio size={13} className="text-primary" />
        <span className="font-display text-[11px] font-semibold tracking-[0.18em] uppercase text-primary/90">
          {t("home.hero.live")}
        </span>
        {LIVE_REQUIRES_PREMIUM && !isPremium && (
          <span className="font-display text-[9px] tracking-[0.18em] uppercase text-primary/55">
            {t("common.premium")}
          </span>
        )}
      </button>

      {/* CRITICAL ALERTS BANNER */}
      <AnimatePresence>
        {hasUrgentTasks && (
          <motion.button
            key="critical-alert"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => navigate("/tasks")}
            className="w-full flex items-center justify-between rounded-2xl px-4 py-3 mb-4 text-left active:opacity-80 transition-opacity overflow-hidden"
            style={{
              background: "hsl(0 72% 51% / 0.08)",
              border: "1px solid hsl(0 72% 51% / 0.30)",
            }}
          >
            <p
              className="text-[12px] font-body leading-snug flex-1 mr-3"
              style={{ color: "hsl(0 72% 72%)" }}
            >
              {tf("alerts.critical_urgent", { count: String(urgentTasks.length) })}
            </p>
            <span
              className="text-[11px] font-display font-semibold tracking-wide shrink-0"
              style={{ color: "hsl(0 72% 65%)" }}
            >
              {t("alerts.view_tasks")}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Existing daily data and actions, presented as one calm, accessible panel. */}
      <HomeTodayPanel
        loading={homeCheckIn.isLoading}
        checkinStatus={checkinStatus}
        onRetryCheckin={homeCheckIn.refresh}
        checkinRecorded={!!todayCheckin}
        nextEvent={homeAgenda.nextEvent}
        agendaStatus={homeAgenda.status}
        onRetryAgenda={homeAgenda.refresh}
        onPrepare={prepareForMoment}
      />

      {/* 4. ACTIVE CHALLENGE — gated off for 15/06 launch. Component preserved
          for v1.1 reactivation (flip CHALLENGE_ENABLED to true). */}
      {CHALLENGE_ENABLED && activeChallenge && !challengeLoading && (
        <motion.button
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={stagger(1.3)}
          onClick={() => navigate("/mirror-challenge")}
          className="w-full rounded-2xl p-4 mb-5 text-left active:scale-[0.98] transition-transform duration-200 group relative overflow-hidden"
          style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.18)" }}
        >
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.85, zIndex: 0 }} src="https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/videos/academia%20du%20futuro.mp4" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,8,16,0.20) 0%, rgba(0,12,20,0.15) 100%)", zIndex: 1 }} />
          <div className="relative" style={{ zIndex: 2 }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-primary/12 flex items-center justify-center">
              <Swords size={13} className="text-primary/80" />
            </div>
            <p className="text-[9px] font-display tracking-[0.2em] uppercase text-primary/90" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>{t("home.challenge.label")}</p>
          </div>
          <p className="font-display text-[13px] font-semibold text-foreground leading-snug mb-2" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.9)" }}>
            {activeChallenge.title} — {tf("challenge.daysProgress", { current: String(activeChallenge.currentProgress), total: String(activeChallenge.targetValue) })}
          </p>
          <div className="w-full h-1.5 rounded-full bg-[hsl(222_18%_14%)] mb-2 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary/70 transition-all duration-700"
              style={{ width: `${Math.round((activeChallenge.currentProgress / activeChallenge.targetValue) * 100)}%` }}
            />
          </div>
          <div className="flex items-center gap-1 text-primary/90 text-[10px] font-display tracking-wide" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.95)" }}>
            <span>{t("home.challenge.cta")}</span>
            <ChevronRight size={10} />
          </div>
          </div>
        </motion.button>
      )}

      {/* Premium nudge card. Background swapped 05/05 from room ex_03.jpeg
          to academia du futuro mp4 (the Supabase Storage video previously
          used by the Desafio Ativo card). Heavier overlay preserved so the
          white CTA stays legible against the cropped video.
          Polish 05/05: bumped minHeight 88->150, dropped title truncate
          (was clipping "Desbloqueie seu Mirror co..."), bumped subtitle
          legibility, and converted the CTA from translucent ghost to a
          solid cyan pill with dark text + glow.
          Shown on ALL platforms (incl. iOS): routes to the /upgrade pitch
          screen, which is itself Apple 3.1.1-safe (static "visit aivyv.com"
          text on iOS, "See plans" CTA on web/Android). */}
      {<motion.button
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        onClick={() => navigate("/upgrade")}
        className="w-full mb-5 rounded-2xl overflow-hidden relative text-left transition-all active:scale-[0.98]"
        style={{
          minHeight: "150px",
          border: "1.5px solid rgba(0,229,255,0.35)",
          boxShadow: "0 0 28px rgba(0,229,255,0.12), inset 0 1px 0 rgba(0,229,255,0.08)",
        }}
      >
        {/* Video background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.85, zIndex: 0 }}
          src="https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/videos/academia%20du%20futuro.mp4"
        />
        {/* Dark overlay (heavier than Desafio card — protects white text legibility
            across the brightest gym frames). */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(0,8,16,0.65) 0%, rgba(0,12,20,0.55) 100%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        {/* Content — title + sub stacked above a solid CTA pill so the title
            never has to compete horizontally with the button (the source of
            the previous "Desbloqueie seu Mirror co..." truncation). */}
        <div className="relative z-10 flex flex-col gap-3 px-5 py-5">
          <div className="flex items-start gap-3">
            <Sparkles size={22} style={{ color: "hsl(187 72% 65%)" }} className="shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p
                className="font-display text-[16px] font-bold text-white leading-snug"
                style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}
              >
                {t("pricing.nudge.title")}
              </p>
              <p
                className="text-[13px] font-body mt-1 text-white/90"
                style={{ textShadow: "0 1px 4px rgba(0,0,0,0.75)" }}
              >
                {isPaymentUiBlocked() ? t("home.upgradeCard.subtitle") : t("pricing.nudge.sub")}
              </p>
            </div>
          </div>
          <span
            className="self-start font-display text-[13px] font-bold px-4 py-2 rounded-lg transition-colors"
            style={{
              background: "hsl(187 72% 55%)",
              color: "hsl(220 35% 8%)",
              boxShadow: "0 4px 16px rgba(0,229,255,0.35), 0 1px 0 rgba(255,255,255,0.18) inset",
            }}
          >
            {t("pricing.nudge.cta")}
          </span>
        </div>
      </motion.button>}

      {/* 5. EXPLORE MODULES PREMIUM CARD */}
      <ExploreModulesCard
        navigate={navigate}
        t={t}
      />

      {/* MODULES INFO BOTTOM SHEET */}
      <AnimatePresence>
        {infoOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[55] bg-black/60 backdrop-blur-sm"
              style={{ touchAction: "none" }}
              onClick={() => setInfoOpen(false)}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="fixed bottom-0 left-0 right-0 z-[60] rounded-t-3xl px-6 pt-5 pb-10 overscroll-contain"
              style={{
                background: "linear-gradient(180deg, hsl(222 30% 10%) 0%, hsl(222 38% 7%) 100%)",
                border: "1px solid rgba(0,229,255,0.18)",
                borderBottom: "none",
                maxWidth: "480px",
                margin: "0 auto",
                maxHeight: "90dvh",
                touchAction: "pan-y",
              }}
              onTouchMove={(e) => e.stopPropagation()}
            >
              <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-5" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-primary/12 border border-primary/22 flex items-center justify-center">
                  <Sparkles size={15} className="text-primary" />
                </div>
                <div>
                  <p className="font-display text-[15px] font-bold text-white">{t("modules.whatAre")}</p>
                  <p className="text-[10px] font-display tracking-wide" style={{ color: "hsl(187 72% 50% / 0.85)" }}>
                    {t("modules.whatAreSub")}
                  </p>
                </div>
              </div>
              <div className="space-y-4 mb-6 overflow-y-auto max-h-[45dvh] overscroll-contain">
                {[
                  { icon: BookOpen, titleKey: "modules.info1Title", bodyKey: "modules.info1Body" },
                  { icon: Target, titleKey: "modules.info2Title", bodyKey: "modules.info2Body" },
                  { icon: GripVertical, titleKey: "modules.info3Title", bodyKey: "modules.info3Body" },
                  { icon: Rocket, titleKey: "modules.info4Title", bodyKey: "modules.info4Body" },
                ].map(({ icon: Icon, titleKey, bodyKey }) => {
                  const title = t(titleKey);
                  const body = t(bodyKey);
                  return (
                  <div key={titleKey} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon size={12} className="text-primary/70" />
                    </div>
                    <div>
                      <p className="font-display text-[12px] font-semibold text-foreground/95 mb-0.5">{title}</p>
                      <p className="text-[11px] font-body text-muted-foreground/85 leading-relaxed">{body}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
              <button
                onClick={() => { setInfoOpen(false); navigate("/modules"); }}
                className="w-full py-3 rounded-2xl font-display text-[13px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
                style={{
                  background: "rgba(0,229,255,0.10)",
                  border: "1px solid rgba(0,229,255,0.28)",
                  color: "hsl(var(--primary))",
                }}
              >
                {t("modules.gotIt")}
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MIRROR SCORE */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2)}
        className="vyv-card p-5 mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(145deg, hsl(220 40% 9%), hsl(225 35% 7%))", border: "1px solid hsl(220 50% 40% / 0.30)", boxShadow: "0 0 60px hsl(220 70% 30% / 0.20), inset 0 0 40px hsl(220 70% 15% / 0.12)", animation: "cardGlow 5s 0.8s ease-in-out infinite" }}
      >
        <div className="absolute pointer-events-none" style={{ top: "-15%", left: "50%", transform: "translateX(-50%)", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, hsl(187 72% 50% / 0.12) 0%, transparent 65%)" }} />
        <div className="absolute pointer-events-none" style={{ bottom: "-10%", right: "-10%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, hsl(265 60% 50% / 0.08) 0%, transparent 65%)" }} />
        <div className="relative" style={{ zIndex: 2 }}>
        <p className="vyv-label mb-3">{t("score.today")}</p>
        <MirrorScore
          score={score}
          contributors={scoreContributors.map(c => ({
            ...c,
            name: c.rawKey ? t(c.rawKey) : c.name,
          }))}
          mirrorMessage={resolveMirrorMessage(mirrorMessage)}
          loading={scoreLoading}
        />
        </div>
      </motion.div>

      {/* TODAY'S DIRECTION */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2.3)}
        className="vyv-card p-4 mb-4 relative overflow-hidden"
        style={{ background: "hsl(220 35% 8%)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-primary/12 flex items-center justify-center">
            <Target size={13} className="text-primary/80" />
          </div>
          <p className="vyv-label">{t("home.direction.title")}</p>
        </div>
        <p className="font-display text-[15px] font-semibold text-foreground leading-snug mb-1.5">
          {directionMain}
        </p>
        <p className="text-muted-foreground text-[12px] leading-relaxed font-body mb-2.5">
          {directionSupport}
        </p>
        <div className="flex items-start gap-2 pt-2.5 border-t border-border/15">
          <Brain size={12} className="text-primary/80 mt-0.5 shrink-0" />
          <p className="text-primary/85 text-[11px] leading-relaxed font-body italic">
            {t(todayFocus.guidance)}
          </p>
        </div>
      </motion.div>

      {/* TODAY AT A GLANCE */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2.5)}
        className="rounded-2xl px-4 py-3 mb-4 flex items-stretch"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.28)" }}
      >
        {[
          {
            Icon: Flame,
            label: t("home.glance.streak"),
            value: streak > 0 ? `${streak}` : t("home.glance.empty"),
            unit: streak > 0 ? t("home.glance.days") : "",
          },
          {
            Icon: Zap,
            label: t("home.glance.energy"),
            value: t(checkInEnergyLabel(todayCheckin?.energy)),
            unit: "",
          },
        ].map(({ Icon, label, value, unit }, i) => (
          <div key={label} className="flex-1 flex flex-col items-center gap-0.5 relative">
            {i > 0 && (
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-[1px]"
                style={{ background: "hsl(187 72% 50% / 0.14)" }}
              />
            )}
            <Icon size={13} style={{ color: "hsl(187 72% 50%)" }} />
            <p className="text-[9px] font-display tracking-wide" style={{ color: "hsl(218 14% 78%)" }}>
              {label}
            </p>
            <p className="font-display text-[13px] font-bold text-white leading-none">
              {value}{unit ? ` ${unit}` : ""}
            </p>
          </div>
        ))}
      </motion.div>

      {/* STATUS BAR */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55, duration: 0.6 }}
        className="flex items-center gap-4 px-1 mb-2"
      >
        {goalLabel && (
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-primary/70" />
            <span className="text-muted-foreground text-[10px] font-display tracking-wide">
              {goalLabel}
            </span>
          </div>
        )}
        {streak > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-accent/70" />
            <span className="text-muted-foreground text-[10px] font-display tracking-wide">
              {streak} {tp("home.status.checkIn", streak)}
            </span>
          </div>
        )}
      </motion.div>

    </MobileLayout>
  );
};

export default MirrorHome;
